import {
    createContext,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';
import { getUserDocument } from '../lib/auth';
import type { UserDocument } from '@schemas/firestore';

interface AuthContextType {
    user: User | null;
    userDoc: UserDocument | null;
    loading: boolean;
    isAuthenticated: boolean;
    isAgeVerified: boolean;
    isUnder13: boolean;
    onboardingCompleted: boolean;
    refreshUserDoc: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    userDoc: null,
    loading: true,
    isAuthenticated: false,
    isAgeVerified: false,
    isUnder13: false,
    onboardingCompleted: false,
    refreshUserDoc: async () => { },
});

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [userDoc, setUserDoc] = useState<UserDocument | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser);

            if (firebaseUser) {
                // Fetch user document from Firestore
                let doc = await getUserDocument(firebaseUser.uid);
                setUserDoc(doc);

                // Auto-link to player roster if not already linked
                if (doc && !doc.linkedPlayerId) {
                    try {
                        const linkUser = httpsCallable(functions, 'linkUserToPlayer');
                        const result = await linkUser();
                        const data = result.data as { linked: boolean; playerId?: string; playerName?: string };

                        if (data.linked && data.playerId) {
                            // Refresh user doc to get updated linked info
                            doc = await getUserDocument(firebaseUser.uid);
                            setUserDoc(doc);
                            console.log(`Auto-linked to player: ${data.playerName}`);
                        }
                    } catch (err) {
                        // Linking is optional, don't block login
                        console.log('Auto-link skipped:', err);
                    }
                }
            } else {
                setUserDoc(null);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Function to refresh user doc (called after onboarding updates)
    const refreshUserDoc = async () => {
        if (user) {
            const doc = await getUserDocument(user.uid);
            setUserDoc(doc);
        }
    };

    const value: AuthContextType = {
        user,
        userDoc,
        loading,
        isAuthenticated: !!user,
        isAgeVerified: userDoc?.ageVerified ?? false,
        isUnder13: userDoc?.isUnder13 ?? false,
        onboardingCompleted: userDoc?.onboardingCompleted ?? false,
        refreshUserDoc,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
